import type { StampDutyResults as Results } from "../_types/calculator.types";
import type { AustralianState } from "../_types/calculator.types";
import { STATE_NAMES } from "../_types/calculator.types";
import { siteConfig } from "@/config/website.config";
import { broker } from "@/config/broker.config";

interface StampDutyResultsProps {
  results: Results;
  inputs: {
    state: AustralianState;
    propertyValue: number;
    isFirstHomeBuyer: boolean;
    isPensioner: boolean;
    propertyType: string;
    purchaseType: string;
  };
}

/**
 * Format number as AUD currency
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Result row component
 */
function ResultRow({
  label,
  value,
  isTotal = false,
  variant = "default",
}: {
  label: string;
  value: number;
  isTotal?: boolean;
  variant?: "default" | "savings" | "grant";
}) {
  if (isTotal) {
    return (
      <div className="flex justify-between items-center pt-4 mt-4 border-t-2 border-primary/20">
        <span className="font-semibold text-foreground">{label}</span>
        <span className="font-bold text-xl text-foreground">{formatCurrency(value)}</span>
      </div>
    );
  }

  const valueColor =
    variant === "savings"
      ? "text-green-600"
      : variant === "grant"
      ? "text-green-600"
      : "text-foreground";

  return (
    <div className="flex justify-between items-center py-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium ${valueColor}`}>
        {variant === "savings" && value > 0 ? "-" : ""}
        {variant === "grant" && value > 0 ? "+" : ""}
        {formatCurrency(value)}
      </span>
    </div>
  );
}

/**
 * Stamp duty calculation results display
 */
export function StampDutyResultsDisplay({ results, inputs }: StampDutyResultsProps) {
  const hasSavings = results.fhbConcession > 0 || results.pensionerConcession > 0;
  const totalSavings = results.fhbConcession + results.pensionerConcession;
  const totalBenefits = results.fhog + totalSavings;

  return (
    <>
      {/* Screen Display */}
      <div className="mt-10 pt-8 border-t border-border print:hidden">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-6">
          Your Results
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Government Fees */}
          <div className="bg-cream rounded-2xl p-6">
            <h4 className="font-serif text-lg mb-4">Government Fees</h4>
            <div className="space-y-1">
              <ResultRow label="Stamp Duty on Property" value={results.stampDuty} />
              <ResultRow label="Mortgage Registration" value={results.mortgageRegistration} />
              <ResultRow label="Transfer Fee" value={results.transferFee} />
              <ResultRow
                label="Total Government Fees"
                value={results.totalGovernmentFees}
                isTotal
              />
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-green-50 rounded-2xl p-6">
            <h4 className="font-serif text-lg mb-4">Your Benefits</h4>
            <div className="space-y-1">
              {results.fhog > 0 && (
                <ResultRow
                  label="First Home Owner Grant"
                  value={results.fhog}
                  variant="grant"
                />
              )}
              {results.fhbConcession > 0 && (
                <ResultRow
                  label="Stamp Duty Concession (applied)"
                  value={results.fhbConcession}
                  variant="savings"
                />
              )}
              {results.pensionerConcession > 0 && (
                <ResultRow
                  label="Pensioner Concession (applied)"
                  value={results.pensionerConcession}
                  variant="savings"
                />
              )}
              {!hasSavings && results.fhog === 0 && (
                <p className="text-muted-foreground text-sm py-3">
                  No concessions or grants apply based on your inputs.
                </p>
              )}
              {(hasSavings || results.fhog > 0) && (
                <ResultRow
                  label="Total Benefits"
                  value={totalBenefits}
                  isTotal
                />
              )}
            </div>
            {hasSavings && (
              <p className="text-xs text-muted-foreground mt-4">
                * Concessions are already applied to reduce your stamp duty amount shown.
              </p>
            )}
          </div>
        </div>

        {/* Net Summary */}
        <div className="mt-6 bg-primary text-primary-foreground rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-primary-foreground/90 text-sm mb-1">
                {results.fhog > 0 ? "Net Cost (after FHOG)" : "Total Cost"}
              </p>
              <p className="text-3xl font-bold">{formatCurrency(results.netCost)}</p>
            </div>
            <div className="text-sm text-primary-foreground/90">
              <p>Total Fees: {formatCurrency(results.totalGovernmentFees)}</p>
              {results.fhog > 0 && <p>Less FHOG: -{formatCurrency(results.fhog)}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Print Layout */}
      <div className="hidden print:block mt-8">
        <div className="border-2 border-gray-300 rounded-lg p-8">
          {/* Print Header */}
          <div className="flex justify-between items-start border-b-2 border-gray-300 pb-6 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{siteConfig.appName}</h1>
              <p className="text-sm text-gray-600 mt-1">Stamp Duty Estimate</p>
            </div>
            <div className="text-right text-sm text-gray-600">
              <p>{broker.name}</p>
              <p>{broker.phone}</p>
              <p>{broker.email}</p>
            </div>
          </div>

          {/* Property Details */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">Property Details</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">State:</span>
                <span className="ml-2 font-medium">{STATE_NAMES[inputs.state]}</span>
              </div>
              <div>
                <span className="text-gray-600">Property Value:</span>
                <span className="ml-2 font-medium">{formatCurrency(inputs.propertyValue)}</span>
              </div>
              <div>
                <span className="text-gray-600">Property Type:</span>
                <span className="ml-2 font-medium capitalize">{inputs.propertyType} residence</span>
              </div>
              <div>
                <span className="text-gray-600">Purchase Type:</span>
                <span className="ml-2 font-medium capitalize">{inputs.purchaseType} home</span>
              </div>
              <div>
                <span className="text-gray-600">First Home Buyer:</span>
                <span className="ml-2 font-medium">{inputs.isFirstHomeBuyer ? "Yes" : "No"}</span>
              </div>
              <div>
                <span className="text-gray-600">Pensioner:</span>
                <span className="ml-2 font-medium">{inputs.isPensioner ? "Yes" : "No"}</span>
              </div>
            </div>
          </div>

          {/* Results Table */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">Government Fees</h2>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b">
                  <td className="py-2 text-gray-600">Stamp Duty on Property</td>
                  <td className="py-2 text-right font-medium">{formatCurrency(results.stampDuty)}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 text-gray-600">Mortgage Registration</td>
                  <td className="py-2 text-right font-medium">{formatCurrency(results.mortgageRegistration)}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 text-gray-600">Transfer Fee</td>
                  <td className="py-2 text-right font-medium">{formatCurrency(results.transferFee)}</td>
                </tr>
                <tr className="border-t-2 border-gray-400">
                  <td className="py-2 font-semibold">Total Government Fees</td>
                  <td className="py-2 text-right font-bold text-lg">{formatCurrency(results.totalGovernmentFees)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Benefits */}
          {(results.fhog > 0 || hasSavings) && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-3">Benefits Applied</h2>
              <table className="w-full text-sm">
                <tbody>
                  {results.fhog > 0 && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">First Home Owner Grant</td>
                      <td className="py-2 text-right font-medium text-green-700">+{formatCurrency(results.fhog)}</td>
                    </tr>
                  )}
                  {results.fhbConcession > 0 && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">Stamp Duty Concession (already applied)</td>
                      <td className="py-2 text-right font-medium text-green-700">-{formatCurrency(results.fhbConcession)}</td>
                    </tr>
                  )}
                  {results.pensionerConcession > 0 && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">Pensioner Concession (already applied)</td>
                      <td className="py-2 text-right font-medium text-green-700">-{formatCurrency(results.pensionerConcession)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Net Cost */}
          <div className="bg-gray-100 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Estimated Net Cost</span>
              <span className="text-2xl font-bold">{formatCurrency(results.netCost)}</span>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="text-xs text-gray-500 border-t pt-4">
            <p className="mb-2">
              <strong>Disclaimer:</strong> This estimate is provided for informational purposes only
              and should not be relied upon as financial advice. Actual costs may vary based on your
              specific circumstances. Please consult with {broker.name} at {siteConfig.appName} for
              accurate figures and professional advice.
            </p>
            <p>
              ACL {siteConfig.business.acl} via {siteConfig.business.aclProvider} |
              Generated on {new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
